/**
 * Floating score labels at catch / merge positions.
 */

import * as C from "../config/constants.js";
import { fallCountdownFontSizePx } from "../layout/mockup-ui-scale.js";

var DEFAULT_DURATION_MS = 700;
var DEFAULT_RISE_PX = 28;
var POP_IN_MS = 120;
var SCORE_POPUP_FONT_FAMILY = "'Baloo 2', 'Fredoka', sans-serif";
var SCORE_POPUP_FONT_WEIGHT = 500;
var SCORE_POPUP_STROKE_INK = "27, 94, 125";
var SCORE_POPUP_FILL_SIZE_RATIO = 0.97;
var SCORE_POPUP_STROKE_WIDTH_RATIO = 0.16;
/** Sub-pixel floor so tiny popups never disappear; stays proportional above this. */
var SCORE_POPUP_STROKE_WIDTH_MIN = 0.75;

/**
 * @param {number} peakFontPx
 */
export function computeScorePopupStrokeWidth(peakFontPx) {
  var peak = Number(peakFontPx);
  if (!isFinite(peak) || peak <= 0) return SCORE_POPUP_STROKE_WIDTH_MIN;
  return Math.max(SCORE_POPUP_STROKE_WIDTH_MIN, peak * SCORE_POPUP_STROKE_WIDTH_RATIO);
}

/**
 * Peak popup font size — ~50% of Fall countdown peak (countdown uses flaskH × 0.6).
 *
 * @param {{ runwayFlaskDisplayHeight?: number }|null} [scale]
 * @param {{ scaleSide?: number }|null} [mockupScale]
 * @param {object} [cfg]
 */
export function computeScorePopupPeakFontPx(scale, mockupScale, cfg) {
  cfg = cfg || (C.RUN_CONFIG || {});
  var countdownRatio = cfg.FALL_COUNTDOWN_FLASK_HEIGHT_RATIO != null
    ? Number(cfg.FALL_COUNTDOWN_FLASK_HEIGHT_RATIO)
    : 0.6;
  var popupRatio = cfg.SCORE_POPUP_COUNTDOWN_HEIGHT_RATIO != null
    ? Number(cfg.SCORE_POPUP_COUNTDOWN_HEIGHT_RATIO)
    : 0.5;
  var flaskH = scale && scale.runwayFlaskDisplayHeight != null
    ? Number(scale.runwayFlaskDisplayHeight)
    : NaN;
  if (isFinite(flaskH) && flaskH > 0) {
    return Math.max(1, Math.round(flaskH * countdownRatio * popupRatio));
  }
  var basePx = cfg.FALL_COUNTDOWN_FONT_BASE_PX != null ? cfg.FALL_COUNTDOWN_FONT_BASE_PX : 72;
  var side = mockupScale && mockupScale.scaleSide != null ? Number(mockupScale.scaleSide) : 1;
  var countdownPeak = fallCountdownFontSizePx(side, basePx);
  return Math.max(1, Math.round(countdownPeak * popupRatio));
}

/**
 * @param {number} x
 * @param {number} y
 * @param {string} text
 * @param {{ durationMs?: number, risePx?: number }} [opts]
 */
export function createScorePopup(x, y, text, opts) {
  opts = opts || {};
  return {
    x: x,
    y: y,
    text: text,
    startMs: Date.now(),
    durationMs: opts.durationMs != null ? opts.durationMs : DEFAULT_DURATION_MS,
    risePx: opts.risePx != null ? opts.risePx : DEFAULT_RISE_PX,
    scale: 0.4,
  };
}

/**
 * @param {ReturnType<typeof createScorePopup>[]} popups
 * @param {number} [nowMs]
 */
export function pruneScorePopups(popups, nowMs) {
  if (!popups || !popups.length) return popups;
  var now = nowMs != null ? nowMs : Date.now();
  var out = [];
  for (var i = 0; i < popups.length; i++) {
    var p = popups[i];
    if (now - p.startMs < p.durationMs + 50) out.push(p);
  }
  return out;
}

/**
 * @param {CanvasRenderingContext2D} ctx
 * @param {ReturnType<typeof createScorePopup>[]} popups
 * @param {number} [nowMs]
 * @param {{ peakFontPx?: number, popScaleMin?: number }} [opts]
 */
export function drawScorePopups(ctx, popups, nowMs, opts) {
  if ((!ctx && !(opts && opts.playfield)) || !popups || !popups.length) return;
  opts = opts || {};
  var cfg = C.RUN_CONFIG || {};
  var peakFontPx = opts.peakFontPx != null
    ? Number(opts.peakFontPx)
    : computeScorePopupPeakFontPx(null, null, cfg);
  if (!isFinite(peakFontPx) || peakFontPx <= 0) peakFontPx = 24;
  var popMin = opts.popScaleMin != null
    ? Number(opts.popScaleMin)
    : (cfg.FALL_COUNTDOWN_POP_SCALE_MIN != null ? Number(cfg.FALL_COUNTDOWN_POP_SCALE_MIN) : 0.5);
  if (!isFinite(popMin) || popMin <= 0) popMin = 0.5;
  popMin = Math.min(1, popMin);

    var playfield = opts.playfield;
    var now = nowMs != null ? nowMs : Date.now();
    for (var i = 0; i < popups.length; i++) {
      var p = popups[i];
      var elapsed = now - p.startMs;
      if (elapsed < 0 || elapsed >= p.durationMs) continue;
      var t = elapsed / p.durationMs;
      var popT = Math.min(1, elapsed / POP_IN_MS);
      var scale = popMin + popT * (1 - popMin);
      if (t > 0.55) scale *= 1 - (t - 0.55) / 0.45;
      scale = Math.max(0, scale);
      var alpha = t < 0.12 ? t / 0.12 : (t > 0.55 ? 1 - (t - 0.55) / 0.45 : 1);
      alpha = Math.max(0, Math.min(1, alpha));
      var y = p.y - p.risePx * t;
      var fillFontPx = Math.max(1, Math.round(peakFontPx * SCORE_POPUP_FILL_SIZE_RATIO));
      var strokeWidth = computeScorePopupStrokeWidth(peakFontPx);
      if (playfield && typeof playfield.overlay === "function") {
        playfield.overlay({
          key: "score-" + i + "-" + p.text,
          kind: "label",
          x: p.x,
          y: y,
          text: p.text,
          fontSize: fillFontPx,
          fontWeight: SCORE_POPUP_FONT_WEIGHT,
          fill: 0xffffff,
          strokeFill: 0x1b5e7d,
          stroke: strokeWidth,
          scale: scale,
          alpha: alpha,
          zIndex: 60,
        });
        continue;
      }
      if (!ctx) continue;
      var alphaStr = alpha.toFixed(3);
      ctx.save();
      ctx.translate(p.x, y);
      ctx.scale(scale, scale);
      ctx.font = SCORE_POPUP_FONT_WEIGHT + " " + fillFontPx + "px " + SCORE_POPUP_FONT_FAMILY;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.lineJoin = "round";
      ctx.lineWidth = strokeWidth;
      ctx.strokeStyle = "rgba(" + SCORE_POPUP_STROKE_INK + ", " + alphaStr + ")";
      ctx.fillStyle = "rgba(255, 255, 255, " + alphaStr + ")";
      ctx.strokeText(p.text, 0, 0);
      ctx.fillText(p.text, 0, 0);
      ctx.restore();
    }
}